<template>
    <div class="app-details">
        <pendo-card
            title="Basic Settings"
            body-min-height="0"
            class="settings-group">
            <div
                v-if="showSetting(['adopt'], ['extension', 'web'])"
                class="settings-row app-extension-platform-type">
                <div class="settings-label">
                    Application ID
                </div>
                <div class="settings-content">
                    <span>
                        {{ app.id }}
                    </span>
                </div>
            </div>
            <div class="settings-row app-display-name">
                <div class="settings-label">
                    Display Name
                </div>
                <div class="settings-content">
                    <pendo-editable-content
                        :value="app.displayName || 'Not Set'"
                        :before-exit="onDisplayNameChange">
                        <template #append="{ confirm, cancel, pending }">
                            <pendo-button
                                :loading="pending"
                                theme="app"
                                icon="check"
                                type="primary"
                                @click="confirm" />
                            <pendo-button
                                theme="app"
                                type="secondary"
                                icon="x"
                                @click="cancel" />
                        </template>
                    </pendo-editable-content>
                </div>
            </div>
            <div
                v-if="
                    showSetting(['adopt', 'engage'], ['web'], 'pendoCore') ||
                        showSetting(['engage'], ['extension'], 'pendoCore')
                "
                class="settings-row app-platform">
                <div class="settings-label">
                    Platform
                </div>
                <div class="settings-content">
                    <span class="app-platform-contents">
                        {{ app.platform }}
                    </span>
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension'])"
                class="settings-row app-extension-platform-type">
                <div class="settings-label">
                    Extension Platform Type
                </div>
                <div class="settings-content">
                    <span>
                        {{ extensionPlatformType }}
                    </span>
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension'])"
                class="settings-row extension-domain-patterns">
                <div class="settings-label">
                    Websites
                </div>
                <div class="settings-content">
                    <edit-extension-domain-patterns
                        :submission-error="submissionError"
                        :saving-domain-patterns="savingChanges"
                        :app="app"
                        :websites-modal-alert-link="websitesModalAlertLink"
                        v-on="$listeners" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension'])"
                class="settings-row extension-regular-expressions">
                <div class="settings-label">
                    Selective URLs
                </div>
                <div class="settings-content">
                    <display-edit-extension-regular-expressions
                        :app="app"
                        :can-edit-reg-ex="isSuperUser && showSetting(['engage'], ['extension'])"
                        :submission-error="submissionError"
                        :saving-reg-exp="savingChanges"
                        v-on="$listeners" />
                </div>
            </div>
            <div
                v-if="canShowApiKey"
                class="settings-row">
                <div class="settings-label">
                    <div>API Key</div>
                </div>
                <div class="settings-content">
                    <div>{{ app.apiKey }}</div>
                </div>
            </div>
            <div
                v-if="showSetting(['adopt'], ['extension', 'web'])"
                class="settings-row app-branding">
                <div class="settings-label">
                    <div>Branding</div>
                    <small class="aside">
                        Make it easy to identify apps by adding a favicon. Assigned colors are used in some charts.
                    </small>
                </div>
                <div class="settings-content">
                    <div class="favicon-selection">
                        <p class="favicon-label">
                            Favicon
                        </p>
                        <div class="favicon-button-img">
                            <pendo-icon
                                v-if="!faviconB64"
                                class="set-favicon"
                                type="puzzle-piece"
                                :fill="color"
                                stroke="#000000" />
                            <img
                                v-else
                                class="set-favicon"
                                :src="faviconB64"
                                alt="favicon">
                            <pendo-button
                                theme="app"
                                type="link"
                                label="Replace"
                                @click="displayFaviconModal" />
                            <pendo-favicon-upload
                                :visible="showFaviconModal"
                                :url-favicon="faviconUrl"
                                @fetch-favicon="getFaviconByUrl"
                                @cancel-upload="closeFaviconModal"
                                @upload-favicon="submitFavicon" />
                        </div>
                    </div>
                    <div class="color-selection">
                        <div
                            v-if="!isEditingColor"
                            class="color-display-container">
                            <div class="color-display">
                                <pendo-icon
                                    class="color-display-icon"
                                    type="square"
                                    aria-hidden="true"
                                    :fill="color"
                                    :stroke="color ? color : '#000000'" />
                                <span>{{ color ? color.toUpperCase() : 'App color not set' }}</span>
                            </div>
                            <pendo-button
                                type="link"
                                label="Edit"
                                @click="() => (isEditingColor = !isEditingColor)" />
                        </div>
                        <div
                            v-else
                            class="color-editor">
                            <app-color-picker
                                class="color-picker"
                                :initial-color="color"
                                :swatch-colors="swatchColors"
                                @favicon-color-changed="onColorChange"
                                @cancel-color-change="() => (isEditingColor = !isEditingColor)" />
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="showSetting(['adopt'], ['extension'])"
                class="settings-row flag-designer-launch-url-token">
                <div class="settings-label">
                    Disable Designer Launch URL Token
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'disableDesignerUrlToken'"
                        :value="app.applicationFlags.disableDesignerUrlToken"
                        @change="handleFlagChange($event, 'disableDesignerUrlToken')" />
                </div>
            </div>
            <div
                v-if="subscriptionUsesBrowserIngest && showSetting(['adopt'], ['extension'])"
                class="settings-row flag-extension-inject-identity-function">
                <div class="settings-label">
                    Inject Visitor Identification Function for Browser Scripting
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'extensionInjectIdentityFunction'"
                        :value="app.applicationFlags.extensionInjectIdentityFunction"
                        @change="handleFlagChange($event, 'extensionInjectIdentityFunction')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension', 'web'], 'pendoCore')"
                class="settings-row flag-show-guides-anon-visitors">
                <div>
                    <div class="settings-label">
                        <div>
                            Show Guides to Anonymous Visitors
                        </div>
                        <small class="aside">
                            Turning this flag <strong>on or off</strong> requires
                            <strong>resetting the segment</strong> for each pre-existing guide you’d like this change to
                            take effect on.
                        </small>
                    </div>
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'showGuidesToAnon'"
                        :value="app.applicationFlags.showGuidesToAnon"
                        @change="handleFlagChange($event, 'showGuidesToAnon')" />
                </div>
            </div>
            <div
                v-if="showSetting(['engage'], ['web'], 'pendoCore')"
                class="settings-row track-event-secret">
                <div class="settings-label">
                    Track Event Shared Secret
                </div>
                <div class="settings-content">
                    <div class="track-event-contain">
                        <pendo-button
                            theme="app"
                            type="link"
                            :label="canShowTrackEventSecret ? 'Hide' : 'Show'"
                            @click="() => (canShowTrackEventSecret = !canShowTrackEventSecret)" />
                        <span v-if="canShowTrackEventSecret">
                            {{ app.trackEventSecret }}
                        </span>
                    </div>
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension'])"
                class="settings-row flag-enable-shadow-root">
                <div>
                    <div class="settings-label">
                        <div>
                            Enable shadow root
                        </div>
                    </div>
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'extensionEnableShadowRoot'"
                        :value="app.applicationFlags.extensionEnableShadowRoot"
                        @change="handleFlagChange($event, 'extensionEnableShadowRoot')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    CNAME
                </div>
                <div class="settings-content">
                    <div v-if="!isCnameConfigured">
                        Not Configured
                    </div>
                    <div v-if="isCnameConfigured">
                        Content: <strong>{{ app.contentCname }}</strong>, Data: <strong>{{ app.dataCname }}</strong>
                    </div>
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Disable Agent User Identification Persistence
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'disablePersistence'"
                        :value="app.applicationFlags.disablePersistence"
                        @change="handleFlagChange($event, 'disablePersistence')" />
                </div>
            </div>
            <div
                v-if="showSetting(['engage'], ['web'], 'pendoFeedback')"
                class="settings-row">
                <div class="settings-label">
                    Disable Feedback Auto Init
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="!isOpsUser && updatingAppFlag === 'disableFeedbackAutoInit'"
                        :value="app.applicationFlags.disableFeedbackAutoInit"
                        @change="handleFlagChange($event, 'disableFeedbackAutoInit')" />
                </div>
            </div>
            <div
                v-if="showSetting(['engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Enable Guide Logic Plugin
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'guideMarkdown'"
                        :value="app.applicationFlags.guideMarkdown"
                        @change="handleFlagChange($event, 'guideMarkdown')" />
                </div>
            </div>
            <div
                v-if="showSetting(['engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Count Track Events Received For A Visitor As Time In Application
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'trackEventTimeOnSite'"
                        :value="app.applicationFlags.trackEventTimeOnSite"
                        @change="handleFlagChange($event, 'trackEventTimeOnSite')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Disable client-side storage (Agent cookies and local storage)
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'disableCookies'"
                        :value="app.applicationFlags.disableCookies"
                        @change="handleFlagChange($event, 'disableCookies')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Prevent the web agent from using cookies when local storage is not available
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'localStorageOnly'"
                        :value="app.applicationFlags.localStorageOnly"
                        @change="handleFlagChange($event, 'localStorageOnly')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Use POST requests only to send data to Pendo
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'sendEventsWithPostOnly'"
                        :value="app.applicationFlags.sendEventsWithPostOnly"
                        @change="handleFlagChange($event, 'sendEventsWithPostOnly')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['web'])"
                class="settings-row nested">
                <div class="settings-label">
                    Prefer MutationObserver to 500ms timer, when available
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'preferMutationObserver'"
                        :value="app.applicationFlags.preferMutationObserver"
                        @change="handleFlagChange($event, 'preferMutationObserver')" />
                </div>
                <template
                    v-if="app.applicationFlags.preferMutationObserver && showSetting(['adopt', 'engage'], ['web'])">
                    <div class="settings-label indent">
                        Use MutationObserver on Shadow DOM elements
                    </div>
                    <div class="settings-content">
                        <pendo-checkbox
                            :disabled="updatingAppFlag === 'observeShadowRoots'"
                            :value="app.applicationFlags.observeShadowRoots"
                            @change="handleFlagChange($event, 'observeShadowRoots')" />
                    </div>
                </template>
            </div>
            <div
                v-if="showSetting(['engage'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Disable Global CSS
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'disableGlobalCSS'"
                        :value="app.applicationFlags.disableGlobalCSS"
                        @change="handleFlagChange($event, 'disableGlobalCSS')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension'])"
                class="settings-row flag-disable-extension-text-capture">
                <div>
                    <div class="settings-label">
                        <span class="text-capture-label">
                            Text Capture from Events
                        </span>
                    </div>
                </div>
                <div class="settings-content">
                    {{ app.applicationFlags.disableExtensionTextCapture ? 'No' : 'Yes' }}
                </div>
            </div>
            <div
                v-if="showSetting(['engage'], ['web', 'extension']) || showSetting(['adopt'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Prefer BroadcastChannel for multi-frame support, when available
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'preferBroadcastChannel'"
                        :value="app.applicationFlags.preferBroadcastChannel"
                        @change="handleFlagChange($event, 'preferBroadcastChannel')" />
                </div>
            </div>
            <div
                v-if="showSetting(['engage'], ['web', 'extension']) || showSetting(['adopt'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Use visibility API instead of unload event
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'preventUnloadListener'"
                        :value="app.applicationFlags.preventUnloadListener"
                        @change="handleFlagChange($event, 'preventUnloadListener')" />
                </div>
            </div>
            <div
                v-if="showSetting(['engage'], ['web', 'extension']) || showSetting(['adopt'], ['web'])"
                class="settings-row">
                <div class="settings-label">
                    Attempt click capture on removed elements
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'interceptElementRemoval'"
                        :value="app.applicationFlags.interceptElementRemoval"
                        @change="handleFlagChange($event, 'interceptElementRemoval')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension', 'web'])"
                class="settings-row flag-suggest-page-tags">
                <div class="settings-label">
                    Pendo Suggested Pages
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'suggestPageTags'"
                        :value="app.applicationFlags.suggestPageTags"
                        @change="onSuggestPageTagsChange($event)" />
                </div>
            </div>
            <div
                v-if="extensionPlatformType !== 'Other' && showSetting(['adopt', 'engage'], ['extension'])"
                class="settings-row flag-disable-passdown">
                <div>
                    <div class="settings-label">
                        Disable Inherited Pages and Features
                    </div>
                </div>
                <div class="settings-content">
                    <pendo-checkbox
                        :disabled="updatingAppFlag === 'disablePassDown'"
                        :value="app.applicationFlags.disablePassDown"
                        @change="handleFlagChange($event, 'disablePassDown')" />
                </div>
            </div>
            <div
                v-if="showSetting(['adopt', 'engage'], ['extension'])"
                class="settings-row flag-enable-sibling-selectors">
                <div class="settings-label">
                    Sibling Selectors
                </div>
                <pendo-data-collection-consent-toggle
                    :is-for-customer="isImpersonating"
                    theme="app"
                    :value="app.applicationFlags.siblingSelectors"
                    type="button"
                    :loading="loading"
                    :disabled="updatingAppFlag === 'siblingSelectors'"
                    requires-opt-out
                    @change="handleFlagChange($event, 'siblingSelectors')">
                    <template #disableBody>
                        By turning off this setting, you are opting out of receiving some strong features from Pendo.
                        Tags in your application may be weak and can impact behavior when using Pendo, such as in
                        guides, workflows, paths, etc.
                        <strong> We encourage you to keep this setting on to enable the best Pendo experience.</strong>
                        <br>
                        <br>
                        For questions, please reach out directly to your Pendo support representative or
                        <a href="mailto:support@pendo.io">
                            contact support.
                        </a>
                    </template>

                    <template #enableBody>
                        By enabling Sibling Selectors, I understand that Pendo will collect more data on my application.
                        This setting enables Pendo to share feature tags which use that data to generate a strong tag
                        rule.
                        <br>
                        <br>
                        For questions, please reach out directly to your Pendo support representative or
                        <a href="mailto:support@pendo.io">
                            contact support.
                        </a>
                        <br>
                        <br>
                    </template>
                </pendo-data-collection-consent-toggle>
            </div>
            <div class="settings-row nested flag-enable-page-event-properties">
                <div class="settings-label">
                    Page Event Properties
                </div>
                <div class="settings-content">
                    <pendo-data-collection-consent-toggle
                        :is-for-customer="isImpersonating"
                        theme="app"
                        :value="pageEventPropertiesButtonValue"
                        type="button"
                        :loading="loading"
                        legal-title="Enable Page Event Properties"
                        :disabled="updatingApp"
                        requires-opt-out
                        @change="configurePageEventProperties($event)">
                        <template #disableBody>
                            By turning off this setting, you are opting out of collecting page event properties. Tags in
                            your application that use page event properties may break and can impact behavior when using
                            Pendo, such as in guides, workflows, paths, etc.
                            <strong>
                                We encourage you to keep this setting on to enable the best Pendo experience.</strong>
                            <br>
                            <br>
                            For questions, please reach out directly to your Pendo support representative or
                            <a href="mailto:support@pendo.io">
                                contact support.
                            </a>
                        </template>

                        <template #enableBody>
                            The feature you are enabling may allow you to collect personal data and send it to Pendo.
                            Please be mindful of your company's policies regarding personal data collection and
                            processing, and the setting you select when configuring Page Event Properties.
                            <br>
                            <br>
                            For questions, please reach out directly to your Pendo support representative or
                            <a href="mailto:support@pendo.io">
                                contact support.
                            </a>
                            <br>
                            <br>
                        </template>
                    </pendo-data-collection-consent-toggle>
                </div>
                <template v-if="app.applicationFlags.pageBasedEventProperties">
                    <div class="settings-label indent">
                        Collect Page Titles
                    </div>
                    <div class="settings-content">
                        <pendo-data-collection-consent-toggle
                            :is-for-customer="isImpersonating"
                            theme="app"
                            :value="collectPageTitlesButtonValue"
                            type="button"
                            :loading="loading"
                            legal-title="Enable Collect Page Titles"
                            :disabled="updatingApp || !pageEventPropertiesButtonValue"
                            requires-opt-out
                            @change="configureCollectPageTitles($event, 'document_title', true, 'document.title')">
                            <template #disableBody>
                                By turning off this setting, you are opting out of collecting page titles. Tags in your
                                application that use page titles may break and can impact behavior when using Pendo,
                                such as in guides, workflows, paths, etc.
                                <strong>
                                    We encourage you to keep this setting on to enable the best Pendo
                                    experience.</strong>
                                <br>
                                <br>
                                For questions, please reach out directly to your Pendo support representative or
                                <a href="mailto:support@pendo.io">
                                    contact support.
                                </a>
                            </template>

                            <template #enableBody>
                                The feature you are enabling may allow you to collect personal data and send it to
                                Pendo. Please be mindful of your company's policies regarding personal data collection
                                and processing, and the setting you select when configuring Collect Page Titles.
                                <br>
                                <br>
                                For questions, please reach out directly to your Pendo support representative or
                                <a href="mailto:support@pendo.io">
                                    contact support.
                                </a>
                                <br>
                                <br>
                            </template>
                        </pendo-data-collection-consent-toggle>
                    </div>
                </template>
            </div>
        </pendo-card>
    </div>
</template>
<script>
import PendoCard from '@/components/card/pendo-card.vue';
import PendoEditableContent from '@/components/editable-content/pendo-editable-content.vue';
import PendoButton from '@/components/button/pendo-button.vue';
import EditExtensionDomainPatterns from './app-details-subcomponents/edit-extension-domain-patterns.vue';
import DisplayEditExtensionRegularExpressions from './app-details-subcomponents/display-edit-extension-regular-expressions.vue';
import PendoCheckbox from '@/components/checkbox/pendo-checkbox.vue';
import PendoIcon from '@/components/icon/pendo-icon.vue';
import PendoDataCollectionConsentToggle from '@/composites/data-collection-consent-toggle/pendo-data-collection-consent-toggle.vue';
import AppColorPicker from './app-details-subcomponents/app-color-picker.vue';
import PendoFaviconUpload from '@/composites/favicon-upload/pendo-favicon-upload.vue';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import Vibrant from 'node-vibrant';

export default {
    name: 'PendoAppDetails',
    components: {
        PendoCard,
        PendoEditableContent,
        PendoButton,
        PendoCheckbox,
        PendoIcon,
        PendoDataCollectionConsentToggle,
        PendoFaviconUpload,
        AppColorPicker,
        EditExtensionDomainPatterns,
        DisplayEditExtensionRegularExpressions
    },
    props: {
        app: {
            type: Object,
            required: true
        },
        faviconUrl: {
            type: Object,
            default: () => {}
        },
        subscription: {
            type: Object,
            required: true
        },
        updatingAppFlag: {
            type: String,
            required: true
        },
        updatingApp: {
            type: Boolean,
            required: true
        },
        submissionError: {
            type: Object,
            default: () => {}
        },
        savingChanges: {
            type: Boolean,
            default: false
        },
        isSuperUser: {
            type: Boolean,
            default: false
        },
        isOpsUser: {
            type: Boolean,
            default: false
        },
        consumingPlatform: {
            type: String,
            required: true
        },
        engageCoreEnabled: {
            type: Boolean,
            default: false
        },
        engageFeedbackEnabled: {
            type: Boolean,
            default: false
        },
        isImpersonating: {
            type: Boolean,
            default: false
        },
        websitesModalAlertLink: {
            type: String,
            required: true
        }
    },
    data () {
        return {
            loading: false,
            color: '',
            faviconB64: '',
            showFaviconModal: false,
            isEditingColor: false,
            swatchColors: [],
            canShowTrackEventSecret: false
        };
    },
    computed: {
        extensionPlatformType () {
            return this.app.extensionPlatformTypeDisplayName || 'Other';
        },
        pageEventPropertiesButtonValue () {
            return this.app.applicationFlags.pageBasedEventProperties;
        },
        collectPageTitlesButtonValue () {
            return (
                this.app.applicationFlags.pageBasedEventProperties &&
                get(this.app, 'eventPropertyConfigurations', []).some((ep) => ep.name === 'document_title')
            );
        },
        subscriptionUsesBrowserIngest () {
            return get(this.subscription, 'featureFlags.enableExtensionBrowserIngest', false);
        },
        isCnameConfigured () {
            return !!(this.app.applicationFlags.cname && this.app.contentCname && this.app.dataCname);
        },
        canShowApiKey () {
            if (this.showSetting('adopt', 'web')) {
                return true;
            }

            return this.showSetting('engage', ['extension', 'web']) || this.isSuperUser;
        }
    },
    watch: {
        faviconB64 () {
            this.getColors(this.faviconB64);
        }
    },
    created () {
        if (this.app) {
            this.faviconB64 = get(this.app, 'faviconB64', '');
            this.color = get(this.app, 'color', '');
        }
    },
    methods: {
        onDisplayNameChange (value) {
            if (value !== this.app.displayName) {
                this.$emit('update-app-name', {
                    app: this.app,
                    updates: { displayName: value }
                });
            }
        },
        handleFlagChange (enabled, flag) {
            this.$emit('update-app-flag', {
                appId: this.app.id,
                flag,
                enabled
            });
        },
        configurePageEventProperties (enabled) {
            if (enabled === this.pageEventPropertiesButtonValue) {
                return;
            }

            this.$emit('update-app', {
                app: this.app,
                updates: {
                    applicationFlags: {
                        pageBasedEventProperties: enabled
                    }
                }
            });
        },
        configureCollectPageTitles (enabled, name, isActive, path) {
            if (enabled === this.collectPageTitlesButtonValue) {
                return;
            }

            const eventPropertyConfigurations = get(this.app, 'eventPropertyConfigurations', []).filter(
                (config) => config.name !== name
            );
            if (enabled) {
                eventPropertyConfigurations.push({ name, isActive, path });
            }
            this.$emit('update-app', {
                app: this.app,
                updates: {
                    eventPropertyConfigurations
                }
            });
        },
        onSuggestPageTagsChange (event) {
            this.$emit('update-suggest-page-tags', {
                appId: this.app.id,
                flag: 'suggestPageTags',
                enabled: event
            });
        },
        async getColors (favicon) {
            if (favicon) {
                await Vibrant.from(favicon).getPalette((err, palette) => {
                    if (palette.Vibrant) {
                        this.color = palette.Vibrant.hex;
                        this.swatchColors = uniq([
                            palette.Vibrant.hex,
                            palette.DarkMuted.hex,
                            palette.DarkVibrant.hex,
                            palette.LightMuted.hex,
                            palette.LightVibrant.hex,
                            palette.Muted.hex
                        ]);
                    }
                });
            } else {
                this.color = '';
                this.swatchColors = [];
            }
        },
        onColorChange (newColor) {
            this.isEditingColor = false;
            this.color = newColor;
            if (this.color !== '' && this.color !== null) {
                this.$emit('update-app', {
                    app: this.app,
                    updates: {
                        color: this.color
                    }
                });
            }
        },
        displayFaviconModal () {
            this.showFaviconModal = true;
        },
        getFaviconByUrl (url) {
            this.$emit('fetch-url', url);
        },
        closeFaviconModal () {
            this.showFaviconModal = false;
        },
        submitFavicon (event) {
            this.faviconB64 = event;
            this.showFaviconModal = false;
            this.$emit('update-app', {
                app: this.app,
                updates: {
                    faviconB64: this.faviconB64
                }
            });
        },
        showSetting (intendedUIs, platforms, engageRequirments = '') {
            if (!this.app) {
                return false;
            }

            if (this.consumingPlatform === 'engage' && engageRequirments !== '') {
                if (engageRequirments === 'pendoCore') {
                    return (
                        platforms.includes(get(this.app, 'platform', '')) &&
                        intendedUIs.includes(this.consumingPlatform) &&
                        this.engageCoreEnabled
                    );
                }

                if (engageRequirments === 'pendoFeedback') {
                    return (
                        platforms.includes(get(this.app, 'platform', '')) &&
                        intendedUIs.includes(this.consumingPlatform) &&
                        this.engageFeedbackEnabled
                    );
                }
            }

            return platforms.includes(get(this.app, 'platform', '')) && intendedUIs.includes(this.consumingPlatform);
        }
    }
};
</script>
<style lang="scss" scoped>
.app-details {
    display: grid;
    grid-gap: 32px;

    ::v-deep .aside {
        font-weight: normal;
        color: $color-gray-70;
    }

    ::v-deep .pendo-card__body {
        padding-top: 0;
        padding-bottom: 0;
    }

    ::v-deep .settings-row {
        border-bottom: 1px solid $color-gray-40;
        min-height: 40px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 16px;
        align-items: center;

        .settings-label {
            font-weight: 600;
            padding: 8px 0;
            width: fit-content;

            .keyboard-shortcut-aside {
                padding-top: 5px;
            }
        }

        .settings-content {
            padding: 8px 0;

            .extension-color-list {
                display: flex;
                align-items: center;
                gap: 10px;
            }
        }

        &:last-of-type {
            border-bottom: none;
        }

        .favicon-color-chooser__favicon {
            max-width: 32px;
            max-height: 32px;
        }
    }

    ::v-deep .nested {
        grid-row-gap: 0px;
    }
}

.text-capture-label {
    display: inline-block;
}
.favicon-button-img {
    display: flex;
    gap: 10px;
    align-items: center;
}
.favicon-label {
    font-weight: 600;
    margin-top: 6px;
    margin-bottom: 6px;
}
.color-display {
    display: flex;
    align-items: center;
    gap: 5px;
}

.track-event-contain {
    display: flex;
    gap: 5px;
    align-items: center;
}

.app-platform-contents {
    text-transform: capitalize;
}

.indent {
    margin-left: 30px;
}
</style>
